import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Switch,
  Box,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useState } from 'react';
import { columnsDataPdfTemplateDetail } from 'variables/columnsData';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { AddIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { usePdfTemplate } from 'hooks/usePdfTemplate';
import { checkForbidden } from 'helpers/checkForbidden';
import SearchTable2 from 'components/Tables/SearchTable2';
import { useHistory, useParams } from 'react-router-dom';
// import Editor from 'components/Editor/EmailEditor';
import Editor from 'components/Editor/PdfEditor';

function DataTablePdfTemplateDetail() {
  const textColor = useColorModeValue('gray.700', 'white');
  const pdfTemplateActions = usePdfTemplate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isTestOpen, onOpen: onTestOpen, onClose: onTestClose } = useDisclosure();
  const [isCreateDetail, setIsCreateDetail] = useState(true);
  const [currentDetailID, setCurrentDetailID] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [templateDetails, setTemplateDetails] = useState([]);
  const [testData, setTestData] = useState('');
  const [testDataError, setTestDataError] = useState('');
  const [testContent, setTestContent] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const history = useHistory();
  const { template_id } = useParams();
  const toast = useToast();

  // Form for template info
  const templateValidationSchema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
    data_structure: Yup.string()
      .test('is-valid-json', 'Invalid JSON format', (value) => {
        if (!value) return true;
        try { JSON.parse(value); return true; } catch { return false; }
      }),
  });
  const templateFormOptions = {
    resolver: yupResolver(templateValidationSchema),
    defaultValues: { name: '', data_structure: '', isActive: false },
  };
  const {
    register: registerTemplate,
    handleSubmit: handleSubmitTemplate,
    formState: formStateTemplate,
    setValue: setValueTemplate,
    watch: watchTemplate,
  } = useForm(templateFormOptions);
  const { errors: errorsTemplate, isSubmitting: isSubmittingTemplate } = formStateTemplate;

  // Form for template detail
  const detailValidationSchema = Yup.object().shape({
    detailName: Yup.string().required('This field is required'),
  });
  const detailFormOptions = { resolver: yupResolver(detailValidationSchema) };
  const {
    register: registerDetail,
    handleSubmit: handleSubmitDetail,
    formState: formStateDetail,
    reset: resetDetail,
    setValue: setValueDetail,
    control: controlDetail,
    watch: watchDetail,
  } = useForm(detailFormOptions);
  const { errors: errorsDetail, isSubmitting: isSubmittingDetail } = formStateDetail;

  useEffect(() => {
    if (template_id) {
      fetchTemplateDetail();
    }
  }, [template_id]);

  const fetchTemplateDetail = async () => {
    setIsFetching(true);
    try {
      const res = await pdfTemplateActions.getPdfTemplateDetail(template_id);
      if (res.status === 200) {
        const data = res.data.data;
        setTemplateData(data);
        setValueTemplate('name', data.name);
        setValueTemplate('data_structure', data.data_structure ? JSON.stringify(data.data_structure, null, 2) : '');
        setValueTemplate('isActive', data.status === 1);
        setTemplateDetails(data.details || []);
      }
    } catch (err) {
      console.error(err);
      toast({
        description: 'Failed to load template detail',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
    setIsFetching(false);
  };

  async function onSubmitTemplate({ name, data_structure, isActive }) {
    const params = {
      name,
      data_structure: data_structure ? JSON.parse(data_structure) : null,
      status: isActive ? 1 : 2,
    };
    try {
      const res = await pdfTemplateActions.updatePdfTemplate(params, template_id);
      if (res.status === 200) {
        toast({
          description: 'Update PDF Template success',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        fetchTemplateDetail();
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function onSubmitDetail({ detailName, content, isActiveDetail }) {
    const params = {
      name: detailName,
      content: content || '',
      is_active: !!isActiveDetail,
    };

    try {
      let res;
      if (isCreateDetail) {
        params.pdf_template_id = parseInt(template_id);
        res = await pdfTemplateActions.createPdfTemplateDetail(params);
      } else {
        res = await pdfTemplateActions.updatePdfTemplateDetail(params, currentDetailID);
      }

      if (res.status === 200) {
        const message = isCreateDetail
          ? 'Create PDF Template Detail success'
          : 'Update PDF Template Detail success';

        toast({
          description: message,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });

        if (isCreateDetail) {
          onClose();
          resetDetail();
        }

        fetchTemplateDetail();
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editDetail(detailID) {
    const detail = templateDetails.find((d) => d.id === detailID);
    if (detail) {
      resetDetail();
      setValueDetail('detailName', detail.name || '');
      setValueDetail('content', detail.content || '');
      setValueDetail('isActiveDetail', detail.is_active);
      setCurrentDetailID(detailID);
      setIsCreateDetail(false);
      onOpen();
    }
  }

  async function copyDetail(detailID) {
    const detail = templateDetails.find((d) => d.id === detailID);
    if (detail) {
      resetDetail();
      setValueDetail('detailName', detail.name || '');
      setValueDetail('content', detail.content || '');
      setValueDetail('isActiveDetail', detail.is_active);
      setIsCreateDetail(true);
      onOpen();
    }
  }

  const handleOpenCreateDetail = () => {
    resetDetail();
    setValueDetail('isActiveDetail', true);
    setIsCreateDetail(true);
    onOpen();
  };

  const handleOpenTestPdf = (detail) => {
    const dataStr = templateData?.data_structure
      ? JSON.stringify(templateData.data_structure, null, 2)
      : '';
    setTestData(dataStr);
    setTestDataError('');
    setTestContent(detail.content || '');
    onTestOpen();
  };

  const handlePreviewPdf = async () => {
    let parsed;
    try {
      parsed = testData ? JSON.parse(testData) : {};
    } catch {
      setTestDataError('Invalid JSON format');
      return;
    }
    setTestDataError('');
    setIsPreviewing(true);
    try {
      const res = await pdfTemplateActions.previewPdfTemplate({
        content: testContent,
        data: parsed,
      });
      const url = URL.createObjectURL(res.data);
      window.open(url);
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message || 'Failed to preview PDF',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsPreviewing(false);
    }
  };


  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant='ghost'
            size='sm'
            mb={4}
            onClick={() => history.push('/admin/management/pdf-template')}
          >
            Back to List
          </Button>
          <Text
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            PDF Template Detail
          </Text>
        </Flex>
      </CardHeader>
      <CardBody pb={0}>
        {/* Part 1: Template Info */}
        <Card mb={6}>
          <CardHeader>
            <Text fontSize='lg' fontWeight='bold' color={textColor}>
              Information
            </Text>
          </CardHeader>
          <CardBody>
            <form id='pdf-template-info-form' onSubmit={handleSubmitTemplate(onSubmitTemplate)}>
              <Flex direction='row' gap={6} flexWrap='wrap' alignItems='flex-start'>
                <Box flex='0 0 auto'>
                  <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                    Code
                  </FormLabel>
                  <Input
                    fontSize='sm'
                    type='text'
                    value={templateData?.code || ''}
                    size='md'
                    isDisabled
                    bg='gray.50'
                    width='200px'
                  />
                </Box>
                <Box flex='0 0 auto'>
                  <FormControl isRequired>
                    <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                      Name
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      type='text'
                      placeholder='Name'
                      maxLength={250}
                      size='md'
                      width='280px'
                      name='name'
                      {...registerTemplate('name')}
                      isInvalid={errorsTemplate.name}
                      errorBorderColor='red.300'
                    />
                    {errorsTemplate.name?.message && (
                      <Text color='red' fontSize={14} mt='5px'>
                        {errorsTemplate.name?.message}
                      </Text>
                    )}
                  </FormControl>
                </Box>
                <Box flex='1 1 auto' minW='300px'>
                  <FormControl isInvalid={!!errorsTemplate.data_structure}>
                    <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                      Data Structure (JSON)
                    </FormLabel>
                    <Textarea
                      fontSize='sm'
                      placeholder='{"key": "value"}'
                      size='md'
                      name='data_structure'
                      {...registerTemplate('data_structure')}
                      minH='120px'
                      fontFamily='monospace'
                      style={{ whiteSpace: 'pre', overflowX: 'auto' }}
                    />
                    {errorsTemplate.data_structure?.message && (
                      <Text color='red' fontSize={14} mt='5px'>
                        {errorsTemplate.data_structure?.message}
                      </Text>
                    )}
                  </FormControl>
                </Box>
              </Flex>
              <Flex alignItems='center' gap={4} mt={4}>
                <Switch
                  name='isActive'
                  isChecked={watchTemplate('isActive')}
                  onChange={(e) => setValueTemplate('isActive', e.target.checked)}
                  size='sm'
                  colorScheme='blue'
                >
                  {watchTemplate('isActive') ? 'Active' : 'InActive'}
                </Switch>
                <Button
                  colorScheme='blue'
                  type='submit'
                  isLoading={isSubmittingTemplate}
                  size='sm'
                >
                  Save
                </Button>
              </Flex>
            </form>
          </CardBody>
        </Card>

        {/* Part 2: Template Details */}
        <Card pb={0} mb={0}>
          <CardHeader pb={0}>
            <Flex justifyContent='space-between' alignItems='center' width='100%'>
              <Text fontSize='lg' fontWeight='bold' color={textColor}>
                Template Details
              </Text>
              {checkForbidden(140) && (
                <Button
                  size='sm'
                  onClick={handleOpenCreateDetail}
                  leftIcon={<AddIcon />}
                  colorScheme='blue'
                  variant='outline'
                >
                  Add Template Detail
                </Button>
              )}
            </Flex>
          </CardHeader>
          <CardBody pt={2} pb={0}>
            <SearchTable2
              tableData={templateDetails}
              columnsData={columnsDataPdfTemplateDetail}
              totalRow={templateDetails.length}
              edit={editDetail}
              copy={copyDetail}
              listPermissions={[141, 140]}
              actionsActive={['edit', 'copy']}
              loadData={() => {}}
              isFetching={isFetching}
              manualPagination={false}
              enablePagination={false}
            />
          </CardBody>
        </Card>

        {/* Modal for Template Detail */}
        <Modal
          closeOnOverlayClick={false}
          isOpen={isOpen}
          onClose={() => {
            onClose();
            resetDetail();
          }}
          size='6xl'
        >
          <ModalOverlay />
          <form onSubmit={handleSubmitDetail(onSubmitDetail)}>
            <ModalContent maxW='95vw' w='95vw'>
              <ModalHeader>
                {isCreateDetail ? 'Add New PDF Template Detail' : 'Edit PDF Template Detail'}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <FormControl isRequired mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Name
                  </FormLabel>
                  <Input
                    fontSize='sm'
                    ms='4px'
                    type='text'
                    placeholder='Name'
                    maxLength={250}
                    size='md'
                    name='detailName'
                    {...registerDetail('detailName')}
                    isInvalid={errorsDetail.detailName}
                    errorBorderColor='red.300'
                  />
                  {errorsDetail.detailName?.message && (
                    <Text color='red' fontSize={14} mt='5px'>
                      {errorsDetail.detailName?.message}
                    </Text>
                  )}
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                    Content
                  </FormLabel>
                  <Box overflow='hidden' w='100%'>
                    <Editor
                      name='content'
                      control={controlDetail}
                      defaultValue=''
                      onTestPdf={() => handleOpenTestPdf({ content: watchDetail('content') })}
                    />
                  </Box>
                </FormControl>
                <FormControl>
                  <Switch
                    id='isActiveDetail'
                    name='isActiveDetail'
                    {...registerDetail('isActiveDetail')}
                    size='sm'
                    ms='4px'
                  >
                    Active
                  </Switch>
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Flex gap={3}>
                  <Button
                    colorScheme='blue'
                    type='submit'
                    isLoading={isSubmittingDetail}
                  >
                    {isCreateDetail ? 'Create' : 'Update'}
                  </Button>
                  <Button
                    onClick={() => {
                      onClose();
                      resetDetail();
                    }}
                  >
                    Cancel
                  </Button>
                </Flex>
              </ModalFooter>
            </ModalContent>
          </form>
        </Modal>

        {/* Modal Test PDF */}
        <Modal
          isOpen={isTestOpen}
          onClose={onTestClose}
          size='4xl'
          closeOnOverlayClick={false}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Test PDF Preview</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <FormControl>
                <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                  Data (JSON)
                </FormLabel>
                <Textarea
                  value={testData}
                  onChange={(e) => {
                    setTestData(e.target.value);
                    setTestDataError('');
                  }}
                  minH='400px'
                  fontFamily='monospace'
                  fontSize='sm'
                  style={{ whiteSpace: 'pre', overflowX: 'auto' }}
                  placeholder='{"key": "value"}'
                  isInvalid={!!testDataError}
                />
                {testDataError && (
                  <Text color='red' fontSize={14} mt='5px'>
                    {testDataError}
                  </Text>
                )}
              </FormControl>
            </ModalBody>
            <ModalFooter gap={3}>
              <Button
                colorScheme='blue'
                onClick={handlePreviewPdf}
                isLoading={isPreviewing}
              >
                Preview
              </Button>
              <Button onClick={onTestClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
}

export default DataTablePdfTemplateDetail;
